import React, { useState, useCallback, useMemo } from 'react';
import { Upload, FileText, X } from 'lucide-react';
import Spinner from '../../layout/Spinner';
import { Label } from '../Label';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../Tooltip';

const FileUpload = ({
  isResume,
  onFileUpload,
  id,
  selectedFile,
  template = 'advanced',
  label,
  tooltipContent,
  disabled = false,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);

  const allowedTypes = useMemo(
    () => [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/json',
      'text/plain',
    ],
    []
  );

  const fileTypeNames = useMemo(
    () => ({
      'application/pdf': 'PDF',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/msword': 'DOC',
      'application/json': 'JSON',
      'text/plain': 'TXT',
    }),
    []
  );

  const handleDrag = useCallback((e) => {
    if (disabled) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, [disabled]);

  const uploadFile = useCallback(
    async (file) => {
      if (disabled) {
        return;
      }
      setIsUploading(true);
      try {
        await onFileUpload(file);
        setIsUploading(false);
      } catch (uploadError) {
        setError('Error uploading file. Please try again.');
        setIsUploading(false);
      }
    },
    [disabled, onFileUpload]
  );

  const handleFiles = useCallback(
    (file) => {
      if (disabled) {
        return;
      }
      setError(null);
      setShowError(false);
      const lowerName = (file.name || '').toLowerCase();
      const extensionAllowed = ['.pdf', '.docx', '.doc', '.json', '.txt'].some((ext) =>
        lowerName.endsWith(ext)
      );
      const mimeAllowed = file.type ? allowedTypes.includes(file.type) : false;

      if (!(mimeAllowed || extensionAllowed)) {
        setError(
          `Please upload a ${Object.values(fileTypeNames).join(', ')} file`
        );
        setShowError(true);
        return;
      }
      uploadFile(file);
    },
    [allowedTypes, disabled, fileTypeNames, uploadFile]
  );

  const handleDrop = useCallback(
    (e) => {
      if (disabled) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFiles(e.dataTransfer.files[0]);
      }
    },
    [disabled, handleFiles]
  );

  const handleChange = useCallback(
    (e) => {
      if (disabled) {
        return;
      }
      e.preventDefault();
      if (e.target.files && e.target.files[0]) {
        handleFiles(e.target.files[0]);
      }
    },
    [disabled, handleFiles]
  );

  const removeFile = useCallback(() => {
    if (disabled) {
      return;
    }
    onFileUpload(null);
    setError(null);
    setShowError(false);
  }, [disabled, onFileUpload]);

  const renderAdvancedTemplate = () => (
    <div className="space-y-4">
      <div>
        <h3 className="mb-2 text-lg font-semibold text-darkGrey">
          {isResume ? 'Candidate CV' : 'Job description'}
        </h3>
        <p className="text-sm leading-6 text-slateGrey">
          {isResume
            ? 'Upload the CV or resume you want to convert.'
            : 'Upload the job description.'}
        </p>
      </div>
      <div
        className={`relative rounded-xl border border-dashed p-6 transition-colors duration-200 ${
          isDragActive
            ? 'border-primaryBlue bg-sapphire-50'
            : showError
            ? 'border-red-500 bg-red-50'
            : selectedFile
            ? 'border-green-500 bg-green-50'
            : 'border-gray-300 bg-white hover:border-primaryBlue hover:bg-slate-50'
        } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id={`fileInput-${id}`}
          className="hidden"
          accept={allowedTypes.join(',')}
          onChange={handleChange}
          disabled={disabled}
          aria-label={`Upload your ${isResume ? 'resume' : 'job description'}`}
        />
        <label
          htmlFor={`fileInput-${id}`}
          className={`flex min-h-[15rem] flex-col items-center justify-center text-center ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          {isUploading ? (
            <>
              <Spinner className="mb-4" />
              <p className="text-sm font-medium text-darkGrey">Preparing file...</p>
            </>
          ) : selectedFile ? (
            <>
              <FileText className="mb-4 h-10 w-10 text-green-600" />
              <p className="break-all text-sm font-medium text-darkGrey">{selectedFile.name}</p>
              <p className="mt-2 text-sm text-green-700">File ready</p>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  removeFile();
                }}
                disabled={disabled}
                className="absolute right-3 top-3 rounded-md p-1 text-gray-400 transition-colors duration-200 hover:bg-white hover:text-gray-700"
                aria-label="Remove file"
              >
                <X className="h-5 w-5" />
              </button>
            </>
          ) : (
            <>
              <Upload className={`mb-4 h-10 w-10 ${showError ? 'text-red-500' : 'text-primaryBlue'}`} />
              <p className={`text-sm font-medium ${showError ? 'text-red-600' : 'text-darkGrey'}`}>
                {showError ? error : 'Click to upload or drag and drop'}
              </p>
              {!showError && (
                <p className="mt-2 text-sm text-slateGrey">
                  Accepted formats: {Object.values(fileTypeNames).join(', ')}
                </p>
              )}
            </>
          )}
        </label>
      </div>
    </div>
  );

const renderSimpleTemplate = () => (
  <div className="space-y-2 w-full">
    <TooltipProvider>
      <Label
        htmlFor={`fileInput-${id}`}
        className="block mb-2 text-sm font-medium text-gray-700"
      >
        {label}
      </Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`w-full h-32 md:h-40 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-600 hover:border-blue-500 transition-colors ${
              disabled ? 'cursor-not-allowed opacity-60 pointer-events-none' : 'cursor-pointer'
            }`}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onKeyPress={(e) => {
              if (disabled) {
                return;
              }
              if (e.key === 'Enter' || e.key === ' ') {
                document.getElementById(`fileInput-${id}`).click();
              }
            }}
            onClick={() => {
              if (!disabled) {
                document.getElementById(`fileInput-${id}`).click();
              }
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id={`fileInput-${id}`}
              className="hidden"
              accept={allowedTypes.join(',')}
              onChange={handleChange}
              disabled={disabled}
              aria-label={`Upload your ${label}`}
            />
            {isUploading ? (
              <Spinner />
            ) : selectedFile ? (
              <>
                <FileText className="h-12 w-12 text-green-500 mb-2" />
                <p className="text-gray-600">{selectedFile.name}</p>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    removeFile();
                  }}
                  disabled={disabled}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors duration-300"
                  aria-label="Remove file"
                >
                  <X className="h-6 w-6" />
                </button>
              </>
            ) : (
              <div className="text-center px-4">
                <p
                  className={`text-sm ${
                    showError ? 'text-red-600' : 'text-gray-600'
                  }`}
                >
                  {showError
                    ? error
                    : `Click to upload or drag and drop your ${Object.values(
                        fileTypeNames
                      ).join(', ')} file here`}
                </p>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  </div>
);

  return template === 'advanced' ? renderAdvancedTemplate() : renderSimpleTemplate();
};

export default FileUpload;



